import { useEffect, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_Row,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Typography } from "@mui/material";

type Props<T extends Record<string, any>> = {
  data: T[];
  columns: MRT_ColumnDef<T>[];
  isLoading: boolean;
  renderTopToolbarCustomActions: (table: MRT_TableInstance<T>) => JSX.Element;
  emptyRowMessage: string;
  rowCount: string;
  isDescending: boolean;
  defaultSortColumnName: string;
  openModal?: (parameter: T) => void | undefined;
  manualPagination?: boolean;
  handlePagination?: any;
  pagination?: any;
  checkBoxSelection?: boolean;
  setSelectedRows?: any;
};
const CommandOpsDataGrid = <T extends object>(props: Props<T>) => {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [defaultPagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const handleRowSelection = (row: MRT_Row<T>) => {
    setRowSelection((prev) => ({
      [row.id]: row.id in prev ? true : !prev[row.id],
    }));
    props.openModal !== undefined && props.openModal(row.original);
  };

  const table = useMaterialReactTable({
    columns: props.columns,
    data: props.data,
    enableHiding: false,
    enableColumnFilters: false,
    enableStickyHeader: true,
    enableColumnFilterModes: false,
    enableFullScreenToggle: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableDensityToggle: false,
    ...(props.checkBoxSelection && {
      enableRowSelection: props.checkBoxSelection,
    }),
    muiToolbarAlertBannerProps: { sx: { display: "none" } },
    paginationDisplayMode: "pages",
    initialState: {
      density: "compact",
      sorting: [{ desc: props.isDescending, id: props.defaultSortColumnName }],
    },
    state: {
      pagination: props.pagination ?? defaultPagination,
      showProgressBars: props.isLoading,
      ...(!props.checkBoxSelection && { rowSelection: rowSelection }),
    },
    muiPaginationProps: {
      color: "primary",
      rowsPerPageOptions: [25, 50, 100, 200, 300],
    },
    rowCount: Number(props.rowCount),
    manualPagination: props.manualPagination ?? false,
    onPaginationChange: props.handlePagination ?? setPagination,
    muiTableHeadCellProps: {
      sx: (theme) => ({
        background: theme.palette.primary.main,
        borderRight: "1px solid rgba(224,224,224,1)",
        color: "white",
        ".MuiButtonBase-root": {
          color: "white",
          "&.MuiCheckbox-indeterminate": {
            color: "white",
          },
          "&.Mui-checked": {
            color: "white",
          },
        },
      }),
    },
    muiTableBodyRowProps: ({ row }: any) => ({
      onClick: () => handleRowSelection(row),
      selected: rowSelection[row.id],
      hover: !(
        row.original &&
        (row.original.active === false || row.original.deleted === "true")
      ),
      sx: {
        cursor: "pointer",
        bgcolor:
          row.original &&
          (row.original.active === false || row.original.deleted === "true")
            ? "rgb(164 164 164 / 87%)"
            : "inherit",
        "&.MuiTableRow-root.Mui-selected td": {
          bgcolor: "#ccff90",
          color: "black",
        },
      },
    }),
    muiTableBodyCellProps: {
      sx: {
        borderRight: "1px solid #e0e0e0",
      },
    },
    renderEmptyRowsFallback: () => (
      <Typography sx={{ textAlign: "left", pl: 2 }}>
        {props.emptyRowMessage}{" "}
      </Typography>
    ),
    renderTopToolbarCustomActions: ({ table }) =>
      props.renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <div>
          <Typography>Total Rows: {props.rowCount}</Typography>
        </div>
      );
    },
  });

  useEffect(() => {
    if (props.setSelectedRows) {
      props.setSelectedRows(table.getSelectedRowModel().flatRows);
    }
  }, [table.getSelectedRowModel()]);

  return (
    <div>
      <MaterialReactTable table={table} />
    </div>
  );
};

export default CommandOpsDataGrid;
